import React, { useState, useEffect } from 'react';
import { tahunAPI } from '../../services/api';
import './MasterDataManagement.css';

const TahunManagement = () => {
  const [tahuns, setTahuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingTahun, setEditingTahun] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [newTahun, setNewTahun] = useState({
    id: 0,
    tahun: '',
    tahunName: '',
    nextTahun: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    loadTahuns();
  }, [searchTerm, currentPage, pageSize]);

  const loadTahuns = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await tahunAPI.getTahuns(searchTerm);
      
      if (response.success) {
        setTahuns(response.data);
        setTotalCount(response.data.length);
        setTotalPages(Math.ceil(response.data.length / pageSize));
      } else {
        setError(response.message || 'Failed to load tahun levels');
      }
    } catch (err) {
      console.error('Load tahun error:', err);
      setError('An error occurred while loading tahun levels');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTahun = () => {
    setModalMode('create');
    setEditingTahun(null);
    setNewTahun({
      id: 0,
      tahun: '',
      tahunName: '',
      nextTahun: ''
    });
    setShowModal(true);
  };

  const handleEditTahun = (tahun) => {
    setModalMode('edit');
    setEditingTahun(tahun);
    setNewTahun({
      id: tahun.id,
      tahun: tahun.tahun,
      tahunName: tahun.tahunName || '',
      nextTahun: tahun.nextTahun || ''
    });
    setShowModal(true);
  };

  const handleDeleteTahun = async (id) => {
    if (window.confirm(`Are you sure you want to delete tahun with ID ${id}? This will check if any students are associated with this tahun.`)) {
      try {
        const response = await tahunAPI.deleteTahun(id);
        
        if (response.success) {
          loadTahuns();
          console.log('Tahun deleted successfully');
        } else {
          setError(response.message || 'Failed to delete tahun');
        }
      } catch (err) {
        console.error('Delete error:', err);
        setError('An error occurred while deleting the tahun');
      }
    }
  };

  const handleSaveTahun = async () => {
    try {
      let response;
      
      if (modalMode === 'create') {
        response = await tahunAPI.createTahun({
          tahun: newTahun.tahun,
          tahunName: newTahun.tahunName,
          nextTahun: newTahun.nextTahun
        });
      } else {
        response = await tahunAPI.updateTahun(editingTahun.id, {
          id: newTahun.id,
          tahun: newTahun.tahun,
          tahunName: newTahun.tahunName,
          nextTahun: newTahun.nextTahun
        });
      }

      if (response.success) {
        setShowModal(false);
        loadTahuns();
        console.log(`${modalMode === 'create' ? 'Created' : 'Updated'} tahun successfully`);
      } else {
        setError(response.message || `Failed to ${modalMode} tahun`);
      }
    } catch (err) {
      console.error('Save error:', err);
      setError(`An error occurred while ${modalMode === 'create' ? 'creating' : 'updating'} the tahun`);
    }
  };

  if (loading && !tahuns.length) {
    return (
      <div className="master-data-management">
        <div className="loading">Loading tahun levels...</div>
      </div>
    );
  }

  return (
    <div className="master-data-management">
      <div className="master-data-management-header">
        <div className="header-info">
          <h2><i className="fas fa-calendar-alt"></i> Tahun (Grade/Year Level) Management</h2>
          <p>Manage academic year/grade levels (e.g., KHAS, 1, 2, 3, etc.)</p>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="master-data-management-toolbar">
        <div className="search-section">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search tahun levels..."
            className="search-input"
          />
          <button onClick={loadTahuns} className="btn-search">
            <i className="fas fa-search"></i> Search
          </button>
        </div>
        <button onClick={handleCreateTahun} className="btn-create">
          <i className="fas fa-plus"></i> Add New Tahun
        </button>
      </div>

      <div className="table-container">
        <table className="master-data-table">
          <thead>
            <tr>
              <th>No.</th>
              <th>Tahun Code</th>
              <th>Tahun Name</th>
              <th>Next Tahun</th>
              <th>Student Count</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tahuns.length > 0 ? (
              tahuns.map((tahun, index) => (
                <tr key={tahun.id}>
                  <td>{index + 1}</td>
                  <td><strong>{tahun.tahun}</strong></td>
                  <td>{tahun.tahunName}</td>
                  <td>{tahun.nextTahun || '-'}</td>
                  <td>
                    <span className="badge badge-info">{tahun.studentCount || 0} students</span>
                  </td>
                  <td className="actions-column">
                    <button
                      onClick={() => handleEditTahun(tahun)}
                      className="btn-action btn-edit"
                      title="Edit Tahun"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      onClick={() => handleDeleteTahun(tahun.id)}
                      className="btn-action btn-delete"
                      title="Delete Tahun"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="no-data">No tahun levels found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination-section">
          <div className="pagination-info">
            Page {currentPage} of {totalPages} ({totalCount} total tahun levels)
          </div>
          <div className="pagination-controls">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="btn-page"
            >
              First
            </button>
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="btn-page"
            >
              Previous
            </button>
            <span className="page-input">
              <input
                type="number"
                value={currentPage}
                onChange={(e) => setCurrentPage(Math.max(1, Math.min(totalPages, Number(e.target.value))))}
                min="1"
                max={totalPages}
                className="page-number-input"
              />
              <span>of {totalPages}</span>
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="btn-page"
            >
              Next
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="btn-page"
            >
              Last
            </button>
          </div>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(parseInt(e.target.value));
              setCurrentPage(1);
            }}
            className="page-size-select"
          >
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </select>
        </div>
      )}

      {/* Modal for Add/Edit Tahun */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>
                {modalMode === 'create' ? 'Add New Tahun' : `Edit Tahun: ${newTahun.tahun}`}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="modal-close-btn"
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="tahunCode">Tahun Code *</label>
                <input
                  id="tahunCode"
                  type="text"
                  value={newTahun.tahun}
                  onChange={(e) => setNewTahun({ ...newTahun, tahun: e.target.value.trim().toUpperCase() })}
                  placeholder="Enter tahun code (e.g., KHAS, 1, 2, 3, etc.)"
                  maxLength={10}
                  className="form-control"
                  disabled={modalMode === 'edit'} // Don't allow changing tahun code after creation
                />
                <small className="form-help">Unique identifier for the academic year/grade level (cannot be changed after creation)</small>
              </div>

              <div className="form-group">
                <label htmlFor="tahunName">Tahun Name *</label>
                <input
                  id="tahunName"
                  type="text"
                  value={newTahun.tahunName}
                  onChange={(e) => setNewTahun({ ...newTahun, tahunName: e.target.value.trim() })}
                  placeholder="Enter tahun name (e.g., Khas, Satu, Dua, etc.)"
                  maxLength={10}
                  className="form-control"
                />
                <small className="form-help">Display name for the academic year/grade level (e.g., Khas, Satu, Dua, etc.)</small>
              </div>

              <div className="form-group">
                <label htmlFor="nextTahun">Next Tahun</label>
                <input
                  id="nextTahun"
                  type="text"
                  value={newTahun.nextTahun}
                  onChange={(e) => setNewTahun({ ...newTahun, nextTahun: e.target.value.trim().toUpperCase() })}
                  placeholder="Enter next tahun code (e.g., 1, 2, 3, etc.)"
                  maxLength={10}
                  className="form-control"
                />
                <small className="form-help">Next academic year/grade level in the progression (optional)</small>
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTahun}
                disabled={!newTahun.tahun.trim() || !newTahun.tahunName.trim()}
                className="btn btn-primary"
              >
                {modalMode === 'create' ? 'Create Tahun' : 'Update Tahun'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TahunManagement;